import React from 'react'
import styled from 'styled-components'

import { Result } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'

import { loadFacebookSdk } from './load-facebook-sdk'

import type * as KnownSocialPlatformResource from '../model/known-social-platform-resource'

type Props = {
  href: string
  xfbmlClass: KnownSocialPlatformResource.FacebookXfbmlClass
  width: number
  onLoad?: () => void
  onError?: (error: unknown) => void
}

const facebookRenderTimeoutMs = 15_000

const waitForNextFrame = () =>
  new Promise<void>((resolve) => {
    setTimeout(() => resolve(), 1)
  })

const waitForLayout = (element: HTMLElement) =>
  new Promise<void>((resolve) => {
    if (element.offsetWidth > 0) {
      resolve()

      return
    }

    const observer = new ResizeObserver(() => {
      if (element.offsetWidth > 0) {
        observer.disconnect()
        resolve()
      }
    })

    observer.observe(element)
  })

export function FacebookXfbmlEmbed({
  href,
  xfbmlClass,
  width,
  onLoad,
  onError,
}: Props) {
  const ref = React.useRef<HTMLDivElement>(null)
  const onLoadRef = React.useRef(onLoad)
  const onErrorRef = React.useRef(onError)
  const [loaded, setLoaded] = React.useState(false)

  React.useLayoutEffect(() => {
    onLoadRef.current = onLoad
    onErrorRef.current = onError
  })

  React.useEffect(() => {
    const container = ref.current

    if (!container) {
      return
    }

    let cancelled = false

    setLoaded(false)
    container.replaceChildren()

    const embed = document.createElement('div')
    embed.className = xfbmlClass
    embed.dataset.href = href
    embed.dataset.width = width.toString()

    if (xfbmlClass === 'fb-page') {
      embed.dataset.tabs = 'timeline'
      embed.dataset.height = '500'
      embed.dataset.smallHeader = 'false'
      embed.dataset.adaptContainerWidth = 'true'
    }

    container.appendChild(embed)

    const renderTimeout = window.setTimeout(() => {
      if (!cancelled) {
        onErrorRef.current?.(new Error('facebook embed render timeout'))
      }
    }, facebookRenderTimeoutMs)

    void (async () => {
      const result = await loadFacebookSdk()

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (cancelled) {
        return
      }

      if (Result.isFailure(result)) {
        window.clearTimeout(renderTimeout)
        onErrorRef.current?.(result.failure)
        return
      }

      await waitForNextFrame()

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (cancelled) {
        return
      }

      if (container.offsetWidth === 0) {
        await waitForLayout(container)
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (cancelled) {
        return
      }

      try {
        result.success.parse(container, () => {
          if (cancelled) {
            return
          }

          window.clearTimeout(renderTimeout)
          setLoaded(true)
          onLoadRef.current?.()
        })
      } catch (error) {
        window.clearTimeout(renderTimeout)

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!cancelled) {
          onErrorRef.current?.(error)
        }
      }
    })()

    return () => {
      cancelled = true
      window.clearTimeout(renderTimeout)
      container.replaceChildren()
    }
  }, [href, width, xfbmlClass])

  return (
    <Root>
      <EmbedSlot ref={ref} />
      {loaded ? undefined : (
        <LoaderBackdrop>
          <CircularLoader size={24} />
        </LoaderBackdrop>
      )}
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
`

const EmbedSlot = styled.div`
  width: 100%;

  & > span {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
  }

  & iframe {
    color: transparent;
    background-color: var(--appearance-inv-1);
  }
`

const LoaderBackdrop = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
  pointer-events: none;
`
